<template>
  <section
    id="accessibility-toolbar"
    ref="toolbarSection"
    class="a11y-toolbar"
    :aria-label="$t('AccessibilityToolbar.title')"
    tabindex="-1"
  >
    <!-- Tasto tondo FAB -->
    <b-button
      v-if="showFab"
      variant="secondary"
      class="a11y-toolbar__fab text-white"
      @click="togglePanel"
      :aria-label="$t('AccessibilityToolbar.toggleLabel')"
    >
      <svg
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        width="44"
        height="44"
        viewBox="0 0 16 16"
        fill="currentColor"
      >
        <circle
          cx="8"
          cy="8"
          r="7.5"
          fill="none"
          stroke="currentColor"
          stroke-width="0.8"
        />
        <circle cx="8" cy="4.5" r="1.2" />
        <path
          d="M5 6.8h6M8 6.8v2.7M6.2 13l1.8-3.5L9.8 13"
          stroke="currentColor"
          stroke-width="1"
          stroke-linecap="round"
          stroke-linejoin="round"
          fill="none"
        />
      </svg>
    </b-button>

    <!-- Pannello aperto -->
    <transition name="fade">
      <div v-if="isOpen" class="a11y-toolbar__panel">
        <div class="a11y-toolbar__panel-header">
          <h2 class="a11y-toolbar__panel-title">
            {{ $t("AccessibilityToolbar.title") }}
          </h2>
          <button
            class="a11y-toolbar__close"
            type="button"
            @click="togglePanel"
            :aria-label="$t('AccessibilityToolbar.close')"
          >
            <h2 aria-hidden="true">✕</h2>
          </button>
        </div>
        <ul class="a11y-toolbar__toggles">
          <li v-for="control in toggleControls" :key="control.key">
            <b-button
              :variant="
                preferences[control.key] ? 'secondary' : 'outline-secondary'
              "
              class="a11y-toolbar__btn--toggle"
              :aria-pressed="String(preferences[control.key])"
              @click="toggleControl(control.key, control.labelKey)"
            >
              <span>{{ $t(control.labelKey) }}</span>
            </b-button>
          </li>
        </ul>

        <b-button
          variant="outline-secondary"
          class="a11y-toolbar__btn--reset"
          @click="handleReset"
        >
          {{ $t("AccessibilityToolbar.reset") }}
        </b-button>

        <p class="sr-only" aria-live="polite">{{ announcement }}</p>
      </div>
    </transition>
  </section>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { ACCESSIBILITY_TOOLBAR_EVENT } from "@/_utils/accessibility";

export default {
  name: "AccessibilityToolbar",
  data() {
    return {
      isOpen: false,
      announcement: "",
      toggleControls: [
        {
          key: "accessibleColors",
          labelKey: "AccessibilityToolbar.accessibleColors",
        },
        { key: "bigCursor", labelKey: "AccessibilityToolbar.bigCursor" },
        {
          key: "focusHighlight",
          labelKey: "AccessibilityToolbar.focusHighlight",
        },
        {
          key: "reducedMotion",
          labelKey: "AccessibilityToolbar.reducedMotion",
        },
        { key: "easyRead", labelKey: "AccessibilityToolbar.easyRead" },
      ],
    };
  },
  computed: {
    ...mapState("accessibility", ["preferences"]),
    isPublicRoute() {
      const path = this.$route && this.$route.path ? this.$route.path : "";
      return path.startsWith("/login") || path.startsWith("/register");
    },
    showFab() {
      return !this.isOpen && this.isPublicRoute;
    },
  },
  created() {
    if (typeof window !== "undefined") {
      window.addEventListener(
        ACCESSIBILITY_TOOLBAR_EVENT,
        this.handleExternalOpen
      );
    }
  },
  beforeDestroy() {
    if (typeof window !== "undefined") {
      window.removeEventListener(
        ACCESSIBILITY_TOOLBAR_EVENT,
        this.handleExternalOpen
      );
    }
  },
  methods: {
    ...mapActions("accessibility", ["togglePreference", "resetPreferences"]),
    togglePanel() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$nextTick(() => this.focusToolbarSection());
      }
    },
    async toggleControl(key, labelKey) {
      const newValue = await this.togglePreference(key);
      const messageKey = newValue
        ? "AccessibilityToolbar.featureEnabled"
        : "AccessibilityToolbar.featureDisabled";
      this.setAnnouncement(this.$t(messageKey, { feature: this.$t(labelKey) }));
    },
    handleReset() {
      this.resetPreferences();
      this.setAnnouncement(this.$t("AccessibilityToolbar.resetConfirmation"));
    },
    setAnnouncement(message) {
      this.announcement = message;
    },
    handleExternalOpen() {
      if (!this.isOpen) {
        this.isOpen = true;
      }
      this.$nextTick(() => this.focusToolbarSection());
    },
    focusToolbarSection() {
      const section = this.$refs.toolbarSection;
      if (!section) {
        return;
      }

      const prefersReducedMotion =
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      section.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "start",
      });
      section.focus({ preventScroll: true });
    },
  },
};
</script>

<style scoped>
.a11y-toolbar__fab {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  transition: background-color 0.2s, transform 0.2s;
  padding: 0;
}

.a11y-toolbar__fab:hover {
  transform: scale(1.05);
}

.a11y-toolbar__fab:focus {
  outline: 3px solid #e69f00;
  outline-offset: 2px;
}

.a11y-toolbar__panel {
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  padding: 1rem;
  min-width: 280px;
  max-width: 320px;
}

.a11y-toolbar__panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;
}

.a11y-toolbar__panel-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: #333;
}

.a11y-toolbar__close {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #f5f5f5;
  color: #666;
  border: none;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
}

.a11y-toolbar__close:hover {
  background-color: #e0e0e0;
}

.a11y-toolbar__toggles {
  list-style: none;
  padding: 0;
  margin: 0 0 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.a11y-toolbar__btn--toggle {
  width: 100%;
  text-align: left;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-weight: 600;
  font-size: 0.875rem;
}

.a11y-toolbar__btn--reset {
  width: 100%;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-weight: 600;
  font-size: 0.875rem;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}
</style>
